import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  registerFromInvitationSchema,
  type RegisterFromInvitationInput,
} from '@wingseat/shared';
import { ApiError, api } from '../lib/api-client';

export function RegisterPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: async () => {
      const input: RegisterFromInvitationInput = {
        token: token ?? '',
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        mobilePhone: mobilePhone.trim() || null,
      };
      const parsed = registerFromInvitationSchema.safeParse(input);
      if (!parsed.success) {
        const errs: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          const k = issue.path[0]?.toString();
          if (k && !errs[k]) errs[k] = issue.message;
        }
        setFieldErrors(errs);
        throw new Error('validation');
      }
      setFieldErrors({});
      await api.post('/auth/register-from-invitation', parsed.data);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  if (!token) {
    return (
      <CenterCard>
        <p className="text-sm text-rose-300">Ungültiger Einladungslink.</p>
      </CenterCard>
    );
  }

  if (mutation.isSuccess) {
    return (
      <CenterCard>
        <h1 className="text-xl font-semibold text-center">Fast geschafft</h1>
        <p className="mt-3 text-sm text-slate-300">
          Wir haben Dir eine Bestätigungs-E-Mail geschickt. Bitte klick auf den
          Link darin, um Deine E-Mail-Adresse zu bestätigen.
        </p>
        <button
          onClick={() => navigate('/login', { replace: true })}
          className="mt-6 w-full rounded-lg bg-brand-600 px-4 py-2.5 text-base font-medium text-white hover:bg-brand-700"
        >
          Zum Login
        </button>
      </CenterCard>
    );
  }

  const apiErr =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error && mutation.error.message !== 'validation'
        ? 'Registrierung fehlgeschlagen'
        : null;

  return (
    <CenterCard>
      <form onSubmit={onSubmit} className="space-y-5">
        <header className="text-center">
          <h1 className="text-2xl font-semibold">Registrierung</h1>
          <p className="mt-1 text-sm text-slate-400">
            Vervollständige Dein Konto bei WingSeat
          </p>
        </header>

        <Field label="Vorname" error={fieldErrors['firstName']}>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            required
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-base focus:border-brand-500 focus:outline-none"
          />
        </Field>

        <Field label="Nachname" error={fieldErrors['lastName']}>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
            required
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-base focus:border-brand-500 focus:outline-none"
          />
        </Field>

        <Field
          label="Passwort"
          error={fieldErrors['password']}
          hint="Mindestens 10 Zeichen"
        >
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-base focus:border-brand-500 focus:outline-none"
          />
        </Field>

        <Field label="Mobilnummer (optional)" error={fieldErrors['mobilePhone']}>
          <input
            type="tel"
            value={mobilePhone}
            onChange={(e) => setMobilePhone(e.target.value)}
            autoComplete="tel"
            placeholder="+49 …"
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-base focus:border-brand-500 focus:outline-none"
          />
        </Field>

        {apiErr && (
          <div className="rounded-lg border border-rose-900 bg-rose-950/60 px-3 py-2 text-sm text-rose-200">
            {apiErr}
          </div>
        )}

        <button
          type="submit"
          disabled={mutation.isPending}
          className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-base font-medium text-white hover:bg-brand-700 disabled:opacity-60"
        >
          {mutation.isPending ? 'Registriere …' : 'Registrieren'}
        </button>
      </form>
    </CenterCard>
  );
}

function CenterCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900/60 p-6 shadow-xl">
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium">{label}</label>
      {children}
      {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
      {error && <p className="text-xs text-rose-400">{error}</p>}
    </div>
  );
}
